import { httpClient, toClientApiError } from "./httpClient";

export type NedarimWebhookRow = {
  url: string | null;
  matched: boolean;
};

export type NedarimConnectionStatus = {
  configured: boolean;
  mosadNumber: string | null;
  hasApiPassword: boolean;
  apiPasswordMasked: string;
  webhookUrl: string | null;
  hmacEnabled: boolean;
  createKeyUrl: string | null;
  webhooks: {
    transaction: NedarimWebhookRow;
    keva: NedarimWebhookRow;
    siruv: NedarimWebhookRow;
  } | null;
  webhookError: string | null;
  lastSyncAt: string | null;
  syncTransactionsEnabled: boolean;
  syncHokEnabled: boolean;
};

export type NedarimMovement = {
  id: string;
  tenantId: string;
  customerId: string | null;
  subscriptionId: string | null;
  kind: string;
  channel: string;
  nedarimTransactionId: string | null;
  nedarimKevaId: string | null;
  amountAgorot: number;
  currency: string;
  occurredOn: string | null;
  billingPeriod: string | null;
  status: string;
  groupe: string | null;
  payerName: string | null;
  payerPhone: string | null;
  payerZeout: string | null;
  last4: string | null;
  failureReason: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
};

export type NedarimSyncRun = {
  id: string;
  tenantId: string;
  trigger: string;
  status: string;
  transactionsUpserted: number;
  mandatesUpserted: number;
  chargesUpserted: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
};

export type SubscriptionWithPeriod = {
  id: string;
  customerId: string;
  categoryId: string | null;
  title: string;
  status: string;
  amountAgorot: number;
  currency: string;
  paymentArrangement: string | null;
  nedarimKevaId: string | null;
  nedarimChannel: string | null;
  lastChargeStatusKey: string | null;
  lastChargeOn: string | null;
  billingPeriod: string;
  periodChargeStatus: "paid" | "unpaid" | "failed" | "none";
  periodChargeOccurredOn: string | null;
  startsOn: string | null;
  endsOn: string | null;
  createdAt: string;
  updatedAt: string;
  attributes?: Array<{
    fieldKey: string;
    labelHe: string;
    valueType: "string" | "number" | "boolean" | "date" | "enum";
    value: string | number | boolean;
  }>;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export const nedarimApi = {
  getConnection() {
    return wrap(async () => {
      const { data } = await httpClient.get<{ item: NedarimConnectionStatus }>(
        "/settings/nedarim-connection",
      );
      return data.item;
    });
  },

  saveConnection(body: {
    mosadNumber: string;
    apiPassword?: string | null;
    clearApiPassword?: boolean;
    syncTransactionsEnabled?: boolean;
    syncHokEnabled?: boolean;
  }) {
    return wrap(async () => {
      const { data } = await httpClient.put<{ item: NedarimConnectionStatus }>(
        "/settings/nedarim-connection",
        body,
      );
      return data.item;
    });
  },

  registerWebhooks(force = true) {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: NedarimConnectionStatus }>(
        "/settings/nedarim-connection/register-webhooks",
        { force },
      );
      return data.item;
    });
  },

  enableHmac() {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: NedarimConnectionStatus }>(
        "/settings/nedarim-connection/enable-hmac",
        {},
      );
      return data.item;
    });
  },

  runSync() {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: NedarimSyncRun }>(
        "/settings/nedarim-connection/sync",
        {},
      );
      return data.item;
    });
  },

  listSyncRuns() {
    return wrap(async () => {
      const { data } = await httpClient.get<{ items: NedarimSyncRun[] }>(
        "/settings/nedarim-connection/sync-runs",
      );
      return data.items;
    });
  },

  listMovements(params?: {
    billingPeriod?: string;
    customerId?: string;
    subscriptionId?: string;
    status?: string;
    kind?: string;
  }) {
    return wrap(async () => {
      const { data } = await httpClient.get<{ items: NedarimMovement[] }>(
        "/nedarim/movements",
        { params },
      );
      return data.items;
    });
  },

  listSubscriptions(params?: {
    billingPeriod?: string;
    customerId?: string;
    categoryId?: string;
  }) {
    return wrap(async () => {
      const { data } = await httpClient.get<{
        billingPeriod: string;
        items: SubscriptionWithPeriod[];
      }>("/nedarim/subscriptions", { params });
      return data;
    });
  },

  stopSubscription(
    id: string,
    action: "freeze" | "delete" | "enable" | "restore" | "local_only",
  ) {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: unknown }>(
        `/subscriptions/${id}/stop-nedarim`,
        { action },
      );
      return data.item;
    });
  },

  linkSubscription(
    id: string,
    body: { nedarimKevaId: string; nedarimChannel: "credit" | "bank" },
  ) {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: unknown }>(
        `/subscriptions/${id}/link-nedarim`,
        body,
      );
      return data.item;
    });
  },
};
